import { useCallback, useMemo, useState } from "react";
import "./App.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import ResponsiveNavbar from "./components/ResponsiveNavbar/ResponsiveAppBar";
import NotFound from "./pages/NotFound";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.css";
import Messages from "./components/Messages/Messages";
import { ChatPopup } from "./components/ChatPopup/ChatPopup";
import { UserProfile } from "./components/UserProfile/UserProfile";
import { AuthContext, EnterpriseType, UserDetailsContext } from "./contexts";
import { TalkJsContext, S3CredentialsContext } from "./contexts";
import { useTalk } from "./hooks/useTalk";
import { useGetUser } from "./hooks/useGetUser";
import PageLayout from "./components/PageLayout/PageLayout";
import Mixpanel from "./utils/mixpanel";
import PrivacyPolicy from "./pages/Legal/Privacy";
import CreatorAgreement from "./pages/Legal/CreatorAgreement";
import TermsOfUse from "./pages/Legal/TermsOfUse";
import WelcomePage from "./pages/Welcome/WelcomePage";
import Footer from "./components/Footer/Footer";
import Billing from "./pages/Billing/Billing";
import { getStorageUsage } from "./services/storageService";
import Success from "./pages/Billing/Success";
import PaymentCancel from "./pages/Billing/PaymentCancel";
import {
  Enterprise,
  EnterpriseDetails,
  GetEnterprisesForUserResponse,
} from "./proto/api_pb";
import { SubscriptionTier } from "./proto/api_pb";
import { SubscriptionState } from "./proto/api_pb";
import { SignUpSplash } from "./pages/SignupSplash/SignupSplash";
import useGetValidAccessToken from "./hooks/useGetValidAccessToken";
import PricingTable from "./pages/PricingTable/PricingTable";
import { useGetAllUsers } from "./hooks/useGetAllUsers";
import ReactPixel from "react-facebook-pixel";
import DefaultPage from "./pages/DefaultPage";
import { useS3Credentials } from "./hooks/useS3Credentials";
import { FileUploadStatusCard } from "./components/FileUploadStatusCard/FileUploadStatusCard";
import {
  FolderUploadInfo,
  UploadCardStatusInfo,
} from "./components/FileUploadStatusCard/types";
import { UploadedFileInfo } from "./components/FileUploadStatusCard/types";
import { isEnterprise } from "./theme";
import { SharedContentPage } from "./pages/SharedContentPage/SharedContentPage";
import ShareManagement from "./pages/ShareManagement/ShareManagement";
import { hotjar } from "react-hotjar";
import InitialSettings from "./pages/InitialSettings/InitialSettings";
import { TracksPage } from "./pages/Tracks/TracksPage";
import Loading from "./pages/Loading";
import { ExplorePage } from "./pages/ExplorePage/ExplorePage";
import { ProfilePage } from "./pages/ProfilePage/ProfilePage";
import { getPaymentMethod } from "./components/TrackComponents/TrackHandler/trackHandlers";
import { Projects } from "./pages/Projects/Projects";
import { SelectEnterprise } from "./pages/Enterprise/SelectEnterprise";
import { routesWithoutFooter } from "./components/constants";
import { routesWithoutNavbar } from "./components/constants";
import { TeamManagement } from "./pages/TeamManagement/TeamManagement";
import { handleLastActive } from "./services/userService";
import { getAllUserEnterprises } from "./services/enterpriseService";
import { OpenFoldersListType } from "./pages/Tracks/types";
import { DrawerContainer } from "./components/TracksPageComponents/styles";
import { Drawer } from "./components/TracksPageComponents/Drawer";
import { hiddenDrawerRoutes } from "./helper";
import SharedFolderPage from "./pages/SharedContentPage/SharedFolderPage";

const App: React.FC = () => {
  const url: string = window.location.pathname;
  const { loginWithRedirect, getIdTokenClaims, isAuthenticated, isLoading } =
    useAuth0();
  const [userId, setUserId] = useState("");
  const [storageUsage, setStorageUsage] = useState(0);
  const [successReg, setSuccessReg] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean>(false);
  const [uploadingFilesDetailsList, setUploadingFilesDetailsList] = useState<
    UploadedFileInfo[]
  >([]);
  const [uploadingCardsStatusList, setUploadingCardsStatusList] = useState<
    UploadCardStatusInfo[]
  >([]);
  const [conversationId, setConversationId] = useState<string>("");
  const [conversationSubject, setConversationSubject] = useState<string>("");
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [enterprises, setEnterprises] = useState<EnterpriseDetails[]>([]);
  const [allEnterprisesInfo, setAllEnterprisesInfo] = useState<Enterprise[]>(
    []
  );
  const [folderUploadInfo, setFolderUploadInfo] = useState<FolderUploadInfo>();
  const [selectedEnterprise, setSelectedEnterprise] =
    useState<EnterpriseType>();
  const [openFoldersList, setOpenFoldersList] = useState<OpenFoldersListType[]>(
    []
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getValidAccessToken } = useGetValidAccessToken();
  const { allUsers, featuredUsers } = useGetAllUsers();
  const [profileUpdated] = useState(false);

  useEffect(() => {
    hotjar.initialize(
      parseInt(process.env.REACT_APP_HOTJAR_SITE_ID!, 10),
      parseInt(process.env.REACT_APP_HOTJAR_SITE_VERSION!, 10)
    );
  }, []);

  const hideNavbar = routesWithoutNavbar.some((route) =>
    location.pathname.includes(route)
  );

  const hideFooter = routesWithoutFooter.some((route) =>
    location.pathname.includes(route)
  );

  const isPublicPage =
    location.pathname === "/privacy-policy" ||
    location.pathname === "/creator-agreement" ||
    location.pathname === "/terms-of-use" ||
    location.pathname === "/signup" ||
    location.pathname.includes("/share/") ||
    location.pathname.includes("explore") ||
    location.pathname.includes("user-profile");

  const shouldShowDrawer = !hiddenDrawerRoutes.includes(location.pathname);

  const handleSuccess = () => {
    setSuccessReg(true);
  };

  useEffect(() => {
    if (location.pathname === "/login") {
      loginWithRedirect({
        appState: { returnTo: "/my-tracks" },
      });
    }
    if (location.pathname === "/signup") {
      loginWithRedirect({
        appState: { returnTo: "/my-tracks" },
        authorizationParams: { screen_hint: "signup" },
      });
    }
  }, [location.pathname, loginWithRedirect]);

  // This is because we need to add every event on the page to mixpanel
  useEffect(() => {
    Mixpanel.track(window.location.pathname);
    if (process.env.REACT_APP_ENV === "production") {
      ReactPixel.pageView();
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const idToken = await getIdTokenClaims();
        if (idToken && idToken.sub !== userId) {
          setUserId(idToken.sub);
        }
      }
    })();
  }, [getIdTokenClaims, isAuthenticated, userId]);

  const getStorageUsed = useCallback(async () => {
    const accessToken = await getValidAccessToken();
    const storageUse = await getStorageUsage(
      userId,
      accessToken,
      selectedEnterprise?.id
    );
    typeof storageUse === "number"
      ? setStorageUsage(storageUse)
      : setStorageUsage(0);
  }, [getValidAccessToken, selectedEnterprise?.id, userId]);

  const { getUserResponse, getUserError, isUserLoading } = useGetUser(
    userId,
    profileUpdated
  );
  const { s3Credentials: credentials } = useS3Credentials(userId);

  useEffect(() => {
    const handleGetAllEnterprisesInfo = async () => {
      if (getUserResponse && isAuthenticated) {
        const enterpriseIds = getUserResponse
          .getEnterpriseDetailsList()
          .map((enterpriseDetail) => enterpriseDetail.getEnterpriseId());
        const t = await getValidAccessToken();
        const allEnterprisesInfo = await getAllUserEnterprises(
          t,
          enterpriseIds
        );
        if (allEnterprisesInfo instanceof GetEnterprisesForUserResponse) {
          setAllEnterprisesInfo(allEnterprisesInfo.getEnterpriseList());
        }
      }
    };
    handleGetAllEnterprisesInfo();
  }, [getUserResponse, getValidAccessToken, isAuthenticated]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoading && !isAuthenticated && !isPublicPage) {
        navigate("/welcome");
        return;
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isPublicPage, isLoading, navigate]);

  useEffect(() => {
    if (
      getUserResponse?.getUsername() === "" ||
      getUserResponse?.getUsername().includes("dummy_ommuse")
    ) {
      navigate("initial-setting");
      return;
    }
  }, [getUserResponse, navigate]);

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && getUserError && !isPublicPage && !successReg) {
        navigate("/initial-setting");
      } else if (getUserResponse) {
        if (getUserResponse.getUuid().length > 0) {
          Mixpanel.identify(getUserResponse.getUuid());
          Mixpanel.people.set({
            first_name: getUserResponse.getFirstName(),
            last_name: getUserResponse.getLastName(),
            username: getUserResponse.getUsername(),
            $email: getUserResponse.getEmail(),
          });
        }
        getStorageUsed();
        const stripeCustomerId = getUserResponse.getStripeCustomerId();
        const currentPath = window.location.pathname;
        const enterpriseName = selectedEnterprise?.name;
        const isInitialPath = [
          "/",
          "/initial-setting",
          "/signup",
          "/welcome",
        ].includes(currentPath);
        if (!stripeCustomerId) {
          if (enterpriseName === "ommuse") {
            navigate("/pricing-table");
          } else if (isEnterprise && isInitialPath) {
            navigate("/my-tracks");
          }
        } else if (isInitialPath) {
          navigate("/my-tracks");
        }
      }
    }
  }, [
    getStorageUsed,
    getUserError,
    getUserResponse,
    isAuthenticated,
    isLoading,
    isPublicPage,
    navigate,
    selectedEnterprise,
    successReg,
  ]);

  const [chatOpen, setChatOpen] = useState(false);

  const handleChatClick = () => {
    setChatOpen(!chatOpen);
    Mixpanel.track(chatOpen ? "messages_popup_close" : "messages_popup_open");
  };

  const {
    isChatLoading,
    session,
    setInbox,
    setParticipants,
    createConversation,
    autoCreateChat,
    setAutoCreateChat,
    unreadMessages,
  } = useTalk(userId);

  const authContextValue = useMemo(() => ({ userId }), [userId]);
  const userDetailsContextValue = useMemo(
    () => ({
      userName: getUserResponse?.getUsername() ?? "",
      fullName:
        getUserResponse?.getFirstName() + " " + getUserResponse?.getLastName(),
      connectedWallet: getUserResponse?.getWalletAddressesList()[0] ?? "",
      storageUsage: storageUsage,
      stripeCustomerId: getUserResponse?.getStripeCustomerId() ?? "",
      subscriptionState:
        getUserResponse?.getSubscriptionState() ??
        SubscriptionState.SUBSCRIPTION_STATE_UNSPECIFIED,
      subscriptionTier:
        getUserResponse?.getSubscriptionTier() ??
        SubscriptionTier.SUBSCRIPTION_TIER_UNSPECIFIED,
      allUsers: allUsers,
      featuredUsers: featuredUsers,
      hasPaymentMethod: hasPaymentMethod,
      enterprise: selectedEnterprise,
      role: selectedEnterprise?.role ?? "",
      isEnterpriseAccount: selectedEnterprise?.name
        ? selectedEnterprise.name !== "ommuse"
        : false,
      enterprises: getUserResponse?.getEnterpriseDetailsList() ?? [],
    }),
    [
      getUserResponse,
      storageUsage,
      allUsers,
      featuredUsers,
      hasPaymentMethod,
      selectedEnterprise,
    ]
  );

  const s3CredentialsContextValue = useMemo(
    () => ({
      awsAccessKeyId: credentials?.awsAccessKeyId,
      awsSecretAccessKey: credentials?.awsSecretAccessKey,
      awsSessionToken: credentials?.awsSessionToken,
      expiry: credentials?.expiry,
    }),
    [credentials]
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      const handleUseEffect = async () => {
        if (!userId) return;
        const token = await getValidAccessToken();
        const getMethod = await getPaymentMethod(token, userId);
        if (getMethod) setHasPaymentMethod(getMethod.getHasPaymentMethod());
      };
      handleUseEffect();
    }
  }, [getValidAccessToken, isAuthenticated, userId]);

  useEffect(() => {
    const storedEnterpriseJSON = sessionStorage.getItem("enterprise");
    if (getUserResponse?.getUserId()) {
      setEnterprises(getUserResponse.getEnterpriseDetailsList());
      if (getUserResponse.getEnterpriseDetailsList().length === 1) {
        const e = getUserResponse.getEnterpriseDetailsList()[0];
        const newEnterprise: EnterpriseType = {
          id: e.getEnterpriseId(),
          name: e.getEnterpriseName(),
          image:
            allEnterprisesInfo.length > 0
              ? allEnterprisesInfo[0].getImage() ?? ""
              : "",
          role: e.getRole(),
          storageLimit:
            allEnterprisesInfo.length > 0
              ? allEnterprisesInfo[0].getStorageLimit() ?? 0
              : 0,
          masteringCount: allEnterprisesInfo[0]?.getMasteringCount?.() ?? 0,
        };
        sessionStorage.setItem("enterprise", JSON.stringify(newEnterprise));
      }
    }
    if (!storedEnterpriseJSON) {
      if (
        window.location.pathname !== "/initial-setting" &&
        window.location.pathname !== "/pricing-table" &&
        !isLoading &&
        isAuthenticated &&
        enterprises.length > 0 &&
        !isPublicPage
      ) {
        navigate("/select-enterprise");
        return;
      }
    } else {
      const e: EnterpriseType = JSON.parse(storedEnterpriseJSON);
      const matchingEnterprise: EnterpriseDetails | undefined =
        enterprises.find((ent) => ent.getEnterpriseId() === e.id);
      const matchingEnterpriseForImage = allEnterprisesInfo.find(
        (ent) => ent.getId() === e.id
      );
      if (matchingEnterprise) {
        const UpdatedEnterprise: EnterpriseType = {
          id: e.id,
          name: e.name,
          image:
            allEnterprisesInfo.length > 0
              ? matchingEnterpriseForImage?.getImage() ?? ""
              : "",
          role: matchingEnterprise.getRole(),
          storageLimit:
            allEnterprisesInfo.length > 0
              ? matchingEnterpriseForImage?.getStorageLimit() ?? 0
              : 0,
          masteringCount:
            matchingEnterpriseForImage?.getMasteringCount?.() ?? 0,
        };
        sessionStorage.setItem("enterprise", JSON.stringify(UpdatedEnterprise));
        setSelectedEnterprise(UpdatedEnterprise);
      } else setSelectedEnterprise(e);
    }
  }, [
    allEnterprisesInfo,
    enterprises,
    enterprises.length,
    getUserResponse,
    getValidAccessToken,
    isAuthenticated,
    isLoading,
    isPublicPage,
    navigate,
    userId,
  ]);

  useEffect(() => {
    const handler = async () => {
      if (isAuthenticated && selectedEnterprise) {
        const token = await getValidAccessToken();
        handleLastActive(userId, token, selectedEnterprise?.id);
      }
    };
    handler();
  }, [getValidAccessToken, isAuthenticated, selectedEnterprise, userId]);

  useEffect(() => {
    if (
      getUserResponse?.getStripeCustomerId() === "" &&
      selectedEnterprise?.name === "ommuse"
    ) {
      navigate("/pricing-table");
      return;
    }
  }, [enterprises.length, getUserResponse, selectedEnterprise, navigate]);

  if (
    isAuthenticated &&
    !isPublicPage &&
    (isLoading ||
      (!selectedEnterprise &&
        location.pathname !== "/select-enterprise" &&
        window.location.pathname !== "/initial-setting"))
  ) {
    return <Loading />;
  }

  return (
    <>
      <AuthContext.Provider value={authContextValue}>
        <UserDetailsContext.Provider value={userDetailsContextValue}>
          <S3CredentialsContext.Provider value={s3CredentialsContextValue}>
            <TalkJsContext.Provider
              value={{
                handleChatClick,
                setParticipants,
                createConversation,
                setAutoCreateChat,
                conversationId,
                setConversationId,
                conversationSubject,
                setConversationSubject,
                isChatOpen,
                setIsChatOpen,
              }}
            >
              <div
                style={{
                  position: "relative",
                  minHeight: !hideFooter ? "100vh" : 0,
                  display: "flex",
                }}
              >
                {shouldShowDrawer && (
                  <DrawerContainer>
                    <Drawer
                      currentUser={getUserResponse}
                      isChatLoading={isChatLoading}
                      session={session}
                      setInbox={setInbox}
                      setParticipants={setParticipants}
                      createConversation={createConversation}
                      autoCreateChat={autoCreateChat}
                    />
                  </DrawerContainer>
                )}
                <div style={{ flex: 1, paddingBottom: !hideFooter ? 275 : 0 }}>
                  {isUploading && (
                    <FileUploadStatusCard
                      uploadingFilesDetailsList={uploadingFilesDetailsList}
                      setIsUploading={setIsUploading}
                      uploadingCardsStatusList={uploadingCardsStatusList}
                      setUploadingCardsStatusList={setUploadingCardsStatusList}
                      isProcessing={isProcessing}
                      folderUploadInfo={folderUploadInfo}
                    />
                  )}
                  {!hideNavbar && (
                    <ResponsiveNavbar currentUser={getUserResponse} />
                  )}
                  {url !== "/" &&
                    url !== "/messages" &&
                    isAuthenticated &&
                    !location.pathname.includes("/share/") &&
                    !hideFooter && (
                      <ChatPopup
                        chatOpen={chatOpen}
                        handleChatClick={handleChatClick}
                        unreadMessages={unreadMessages}
                      >
                        <Messages
                          isSmallScreen={true}
                          autoCompleteOptions={allUsers}
                          isChatLoading={isChatLoading}
                          session={session}
                          setInbox={setInbox}
                          setParticipants={setParticipants}
                          createConversation={createConversation}
                          autoCreateChat={autoCreateChat}
                        />
                      </ChatPopup>
                    )}
                  <PageLayout hideNavbar={hideNavbar}>
                    <Routes>
                      <Route
                        path="/share-management"
                        element={<ShareManagement />}
                      />
                      <Route path="/not-found" element={<NotFound />} />
                      <Route
                        path="/team-management"
                        element={
                          <TeamManagement currentUser={getUserResponse} />
                        }
                      />
                      <Route
                        path="/privacy-policy"
                        element={<PrivacyPolicy />}
                      />
                      <Route
                        path="/creator-agreement"
                        element={<CreatorAgreement />}
                      />
                      <Route path="/terms-of-use" element={<TermsOfUse />} />
                      <Route
                        path="/explore"
                        element={
                          <ExplorePage session={session} setInbox={setInbox} />
                        }
                      />
                      {/* <Route path="/dashboard" element={<DashboardPage />} /> */}
                      <Route
                        path="/my-tracks"
                        element={
                          <TracksPage
                            setUploadingFilesDetailsList={
                              setUploadingFilesDetailsList
                            }
                            setIsUploading={setIsUploading}
                            setUploadingCardsStatusList={
                              setUploadingCardsStatusList
                            }
                            session={session}
                            setInbox={setInbox}
                            setIsUploadProcessing={setIsProcessing}
                            setFolderUploadInfo={setFolderUploadInfo}
                            setOpenFoldersList={setOpenFoldersList}
                          />
                        }
                      />
                      <Route
                        path={`/share/:id`}
                        element={
                          <SharedContentPage
                            currentUser={getUserResponse}
                            session={session}
                            setInbox={setInbox}
                          />
                        }
                      />
                      <Route
                        path={`/share/:name/:id`}
                        element={
                          <SharedFolderPage isUserLoading={isUserLoading} />
                        }
                      />
                      <Route
                        path={`/share/project/:name/:id`}
                        element={
                          <SharedFolderPage isUserLoading={isUserLoading} />
                        }
                      />
                      <Route
                        path="/initial-setting"
                        element={
                          <InitialSettings handleSuccess={handleSuccess} />
                        }
                      />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route
                        path="/messages"
                        element={
                          <Messages
                            autoCompleteOptions={allUsers}
                            isChatLoading={isChatLoading}
                            session={session}
                            setInbox={setInbox}
                            setParticipants={setParticipants}
                            createConversation={createConversation}
                            autoCreateChat={autoCreateChat}
                          />
                        }
                      />
                      <Route
                        path="/messages/:conversationId/:conversationSubject"
                        element={
                          <Messages
                            autoCompleteOptions={allUsers}
                            isChatLoading={isChatLoading}
                            session={session}
                            setInbox={setInbox}
                            setParticipants={setParticipants}
                            createConversation={createConversation}
                            autoCreateChat={autoCreateChat}
                          />
                        }
                      />
                      <Route
                        path="/user-profile/:usr"
                        element={
                          <UserProfile session={session} setInbox={setInbox} />
                        }
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/not-found" replace={true} />}
                      />
                      {!isEnterprise && (
                        <Route
                          path="/pricing-table"
                          element={<PricingTable />}
                        />
                      )}
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/logout" element={<WelcomePage />} />
                      <Route
                        path="/select-enterprise"
                        element={
                          <SelectEnterprise
                            enterprises={enterprises}
                            allEnterprisesInfo={allEnterprisesInfo}
                          />
                        }
                      />
                      <Route
                        path="/projects"
                        element={
                          <Projects
                            session={session}
                            setInbox={setInbox}
                            openFoldersList={openFoldersList}
                            setOpenFoldersList={setOpenFoldersList}
                          />
                        }
                      />
                      <Route
                        path="/project/:name/:id"
                        element={
                          <TracksPage
                            setUploadingFilesDetailsList={
                              setUploadingFilesDetailsList
                            }
                            setIsUploading={setIsUploading}
                            setUploadingCardsStatusList={
                              setUploadingCardsStatusList
                            }
                            session={session}
                            setInbox={setInbox}
                            setIsUploadProcessing={setIsProcessing}
                            openFoldersList={openFoldersList}
                            setOpenFoldersList={setOpenFoldersList}
                            setFolderUploadInfo={setFolderUploadInfo}
                          />
                        }
                      />
                      <Route
                        path="/project/:name/:id/folder/:folderId"
                        element={
                          <TracksPage
                            setUploadingFilesDetailsList={
                              setUploadingFilesDetailsList
                            }
                            setIsUploading={setIsUploading}
                            setUploadingCardsStatusList={
                              setUploadingCardsStatusList
                            }
                            session={session}
                            setInbox={setInbox}
                            setIsUploadProcessing={setIsProcessing}
                            openFoldersList={openFoldersList}
                            setOpenFoldersList={setOpenFoldersList}
                            setFolderUploadInfo={setFolderUploadInfo}
                          />
                        }
                      />
                      <Route path="/" element={<DefaultPage />} />
                      {!isEnterprise && (
                        <Route path="/billing" element={<Billing />} />
                      )}
                      <Route path="/success" element={<Success />} />
                      <Route
                        path="/payment-cancel"
                        element={<PaymentCancel />}
                      />
                      <Route path="/signup-splash" element={<SignUpSplash />} />
                      <Route path="/signup" element={<WelcomePage />} />
                      <Route path="/login"></Route>
                    </Routes>
                  </PageLayout>
                </div>
                {!hideFooter && <Footer />}
              </div>
            </TalkJsContext.Provider>
          </S3CredentialsContext.Provider>
        </UserDetailsContext.Provider>
      </AuthContext.Provider>
    </>
  );
};

export default App;
